import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { Info, ShieldCheck, AlertTriangle, HelpCircle, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * SmartTooltip — high-end, colorful, accessible tooltip that matches the JTR brand.
 *
 * Variants:
 *   info     → blue/cyan
 *   trust    → emerald/green
 *   warning  → amber
 *   error    → rose/pink
 *   help     → indigo/purple (default)
 *
 * Behavior:
 *   - Hover, focus, and (on touch devices) tap-to-open via long-press click trigger.
 *   - Smooth fade + slide animation, gradient surface, soft shadow, rounded arrow.
 *   - Multiline content supported.
 *   - Optional title/icon for emphasis.
 *
 * Usage:
 *   <SmartTooltip variant="info" title="Email" content="We send your verification link here.">
 *     <span className="text-xs text-gray-400 cursor-pointer">why?</span>
 *   </SmartTooltip>
 */

const VARIANTS = {
  info: {
    bg: 'from-sky-600 via-blue-600 to-indigo-600',
    arrow: 'fill-blue-600',
    ring: 'ring-blue-300/40',
    icon: Info,
  },
  trust: {
    bg: 'from-emerald-500 via-emerald-600 to-teal-600',
    arrow: 'fill-emerald-600',
    ring: 'ring-emerald-300/40',
    icon: ShieldCheck,
  },
  warning: {
    bg: 'from-amber-500 via-amber-600 to-orange-600',
    arrow: 'fill-amber-600',
    ring: 'ring-amber-300/40',
    icon: AlertTriangle,
  },
  error: {
    bg: 'from-rose-500 via-pink-600 to-rose-700',
    arrow: 'fill-pink-600',
    ring: 'ring-rose-300/40',
    icon: AlertTriangle,
  },
  help: {
    bg: 'from-indigo-600 via-purple-600 to-fuchsia-600',
    arrow: 'fill-purple-600',
    ring: 'ring-purple-300/40',
    icon: HelpCircle,
  },
  premium: {
    bg: 'from-purple-600 via-fuchsia-600 to-pink-600',
    arrow: 'fill-fuchsia-600',
    ring: 'ring-fuchsia-300/40',
    icon: Sparkles,
  },
};

export function SmartTooltipProvider({ children }) {
  return (
    <TooltipPrimitive.Provider delayDuration={150} skipDelayDuration={300}>
      {children}
    </TooltipPrimitive.Provider>
  );
}

export const SmartTooltip = React.forwardRef(function SmartTooltip(
  {
    children,
    title,
    content,
    variant = 'help',
    placement = 'top',
    icon = true,
    className,
    contentClassName,
    maxWidth = 280,
    asChild = true,
    open,
    defaultOpen,
    onOpenChange,
    ...rest
  },
  ref
) {
  const v = VARIANTS[variant] || VARIANTS.help;
  const Icon = v.icon;

  return (
    <TooltipPrimitive.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange} delayDuration={120}>
      <TooltipPrimitive.Trigger asChild={asChild} ref={ref} className={cn('inline-flex outline-none', className)} {...rest}>
        {children}
      </TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={placement}
          sideOffset={8}
          collisionPadding={12}
          className={cn(
            'z-[10000] select-none',
            'animate-in fade-in-0 zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'data-[side=top]:slide-in-from-bottom-2 data-[side=bottom]:slide-in-from-top-2',
            'data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2',
          )}
        >
          <div
            style={{ maxWidth }}
            className={cn(
              'relative rounded-xl px-3.5 py-2.5 text-white text-[12.5px] leading-snug shadow-2xl ring-1 ring-white/10',
              'bg-gradient-to-br', v.bg,
              'backdrop-blur-md',
              'before:absolute before:inset-0 before:rounded-xl before:pointer-events-none',
              'before:bg-[radial-gradient(ellipse_at_top_left,rgba(255,255,255,0.18),transparent_60%)]',
              contentClassName
            )}
            data-testid={`smart-tooltip-${variant}`}
          >
            {(title || (icon && Icon)) && (
              <div className="relative flex items-center gap-1.5 mb-1">
                {icon && Icon && <Icon className="w-3.5 h-3.5 text-white/95 drop-shadow" strokeWidth={2.4} />}
                {title && <span className="text-[11px] font-bold tracking-wider uppercase text-white drop-shadow-sm">{title}</span>}
              </div>
            )}
            <div className="relative text-[12.5px] text-white/95 whitespace-pre-line">{content}</div>
            <div className="absolute inset-0 rounded-xl pointer-events-none ring-1 ring-inset ring-white/20" />
          </div>
          <TooltipPrimitive.Arrow className={cn('drop-shadow-lg', v.arrow)} width={13} height={7} />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
});

/**
 * <FieldTooltipIcon> — small "?" icon next to a form label that triggers a SmartTooltip.
 * Designed to be placed inline with the label.
 */
export function FieldTooltipIcon({
  variant = 'help',
  title,
  content,
  placement = 'top',
  className,
  testid,
}) {
  const Icon = (VARIANTS[variant] || VARIANTS.help).icon;
  return (
    <SmartTooltip variant={variant} title={title} content={content} placement={placement} icon={false}>
      <button
        type="button"
        aria-label={title || 'help'}
        data-testid={testid}
        className={cn(
          'inline-flex items-center justify-center w-4 h-4 rounded-full border border-gray-300 text-gray-400',
          'hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50 transition-colors',
          'focus:outline-none focus:ring-2 focus:ring-purple-300 focus:text-purple-600',
          className
        )}
      >
        <Icon className="w-2.5 h-2.5" strokeWidth={2.5} />
      </button>
    </SmartTooltip>
  );
}

export default SmartTooltip;
